import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Spinner,
  Text,
  cn,
} from '@flatfile/baseline'
import { useEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useSearchParams } from 'react-router-dom'
import { ErrorBoundaryErrorPage } from '../../components/Shared/CustomErrorComponent'
import { useEntitlements } from '../../hooks/useEntitlements'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import {
  BillingContent,
  BillingSection,
  BillingWrapper,
  CommitmentVolumeSelector,
  CurrentBadge,
  DisabledOverlay,
  SectionHeader,
  SelectedProductsContext,
} from './components/BillingComponents'
import {
  CancelSubscriptionModalContent,
  ConfirmProAnnualModalContent,
  UpdateSubscriptionModalContent,
} from './components/BillingModals'
import { BillingUsage } from './components/BillingUsage'
import { useBillingPage } from './hooks/useBillingPage'
import { usePaymentAccess } from './hooks/usePaymentAccess'
import {
  Features,
  IPlan,
  Interval,
  PlanType,
  ProductType,
  UsageType,
  filterProductsByMetadata,
  findDefaultProduct,
  findProductByMetadata,
  getProduct,
} from './utilities/billingHelpers'
import { getCurrentCommitmentProduct, getCurrentPlan } from './utilities/stripe'

const BillingPageContent = () => {
  const { environment: activeEnvironment } = useActiveEnvironment()
  const features = activeEnvironment.features as Features
  const { hasEntitlement } = useEntitlements()
  const disableSelfServe = features?.disableSelfServe
  const billingEnabled = hasEntitlement('billingEnabled')
  const PAYGEnabled = hasEntitlement('paygEnabled')

  const {
    productsData,
    handleCheckoutClick,
    setLineItems,
    isGetProductsLoading,
    isCheckoutError,
    isCheckoutLoading,
    subscription,
    lineItems,
    subscriptionItems,
    updateSubscription,
    subscriptionUpdateIsError,
    subscriptionUpdateIsLoading,
    cancelSubscription,
    subscriptionCancellationIsError,
    subscriptionCancellationIsLoading,
  } = useBillingPage()

  const { filesUsage, pdvUsage, freeFileLimit, freePdvLimit } = usePaymentAccess()

  const currentPlan = getCurrentPlan(subscriptionItems)
  const currentFileCommitment = getCurrentCommitmentProduct(subscriptionItems, 'file')
  const currentPDVCommitment = getCurrentCommitmentProduct(subscriptionItems, 'PDV')

  const [searchParams] = useSearchParams()

  const noChange =
    subscriptionItems.length === lineItems.length &&
    subscriptionItems.every((item) => lineItems.map((x) => x.price).includes(item.price.id)) &&
    lineItems.every((item) => subscriptionItems.map((x) => x.price.id).includes(item.price))

  const starterProduct = findProductByMetadata(productsData, PlanType.STARTER)
  const proMonthlyProduct = findProductByMetadata(productsData, PlanType.PROFESSIONAL, Interval.MONTH)
  const proYearlyProduct = findProductByMetadata(productsData, PlanType.PROFESSIONAL, Interval.YEAR)

  const fileUsageProducts = filterProductsByMetadata(productsData, ProductType.USAGE, UsageType.FILE)
  const pdvUsageProducts = filterProductsByMetadata(productsData, ProductType.USAGE, UsageType.PDV)

  const defaultFileUsageProduct = findDefaultProduct(fileUsageProducts)
  const defaultPDVUsageProduct = findDefaultProduct(pdvUsageProducts)

  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(undefined)
  const [selectedFileUsageId, setSelectedFileUsageId] = useState<string | undefined>(undefined)
  const [selectedPDVUsageId, setSelectedPDVUsageId] = useState<string | undefined>(undefined)

  useEffect(() => {
    let newFileUsageId
    let newPDVUsageId
    let newPlanId

    if (subscriptionItems.length > 0) {
      const selectedPlan = subscriptionItems.find((item) => item.product.metadata.productType === 'plan')?.product
      newPlanId = selectedPlan?.id || starterProduct?.id

      const selectedFileUsage = subscriptionItems.find(
        (item) => item.product.metadata.productType === 'usage' && item.product.metadata.usageType === 'file'
      )?.product
      newFileUsageId = selectedFileUsage?.id

      const selectedPDVUsage = subscriptionItems.find(
        (item) => item.product.metadata.productType === 'usage' && item.product.metadata.usageType === 'PDV'
      )?.product
      newPDVUsageId = selectedPDVUsage?.id
    }

    // Default to the other values if not set from subscriptionItems
    setSelectedPlanId(newPlanId || proMonthlyProduct?.id)
    setSelectedFileUsageId(newFileUsageId || defaultFileUsageProduct?.id)
    setSelectedPDVUsageId(newPDVUsageId || defaultPDVUsageProduct?.id)
  }, [
    subscriptionItems,
    defaultFileUsageProduct?.id,
    defaultPDVUsageProduct?.id,
    starterProduct?.id,
    proMonthlyProduct?.id,
  ])

  const getLineItemForProduct = (id: string | undefined) => {
    const product = productsData.find((p) => p.id === id)
    const price = product?.defaultPrice?.id
    if (product?.metadata?.productType !== 'usage') {
      return {
        price,
        quantity: 1,
      }
    }
    return {
      price,
    }
  }

  const inferredLineItems = useMemo(() => {
    const updatedLineItems: any[] = []

    // If no plan selected, don't add any items
    if (selectedPlanId === undefined) {
      return updatedLineItems
    }
    // Add plan. Don't add starter plan since it's free
    if (selectedPlanId !== starterProduct?.id) {
      updatedLineItems.push(getLineItemForProduct(selectedPlanId))
    }

    // If volume discount, add prepaid file quantity
    const filePrepayId = productsData.find((product) => product.id === selectedFileUsageId)?.metadata?.prepayId
    if (filePrepayId) {
      updatedLineItems.push(getLineItemForProduct(filePrepayId))
    }

    // If volume discount, add prepaid pdv quantity
    const pdvPrepayId = productsData.find((product) => product.id === selectedPDVUsageId)?.metadata?.prepayId
    if (pdvPrepayId) {
      updatedLineItems.push(getLineItemForProduct(pdvPrepayId))
    }

    // Add file usage
    updatedLineItems.push(getLineItemForProduct(selectedFileUsageId))

    // Add pdv usage
    updatedLineItems.push(getLineItemForProduct(selectedPDVUsageId))

    return updatedLineItems
  }, [selectedPlanId, selectedFileUsageId, selectedPDVUsageId, starterProduct?.id, productsData])

  useEffect(() => {
    setLineItems(inferredLineItems)
  }, [inferredLineItems])

  // Add static copy data that isn't stored in Stripe
  const starterPlan: IPlan = {
    product: starterProduct!,
    lead: 'Yes, when we say free, we really mean $0. No commitment, no monthly fees, no strings attached.',
    subhead: 'Pay for usage',
    featuresList: {
      title: 'Starter includes',
      sections: [
        {
          features: ['Full developer features'],
        },
        {
          title: 'Free usage includes:',
          features: ['50 files per month', '10 million PDVs per month', '2 admins'],
        },
        {
          title: 'Usage rates after free allowance:',
          features: ['$2.00 per file', '$2.50 per million PDV'],
        },
      ],
    },
  }

  const proFeaturesList = {
    title: 'Full developer features, plus',
    sections: [
      {
        features: [
          'Whitelabeling',
          'SSO/SAML',
          'Unlimited admins',
          'Data Processing Agreement (DPA)',
          'Premium support',
          'Customer success team',
          'Usage rates after free allowance: $2.00 per file, $2.50 per million PDV or choose volume discounts below',
        ],
      },
    ],
  }

  const proMonthlyPlan: IPlan = {
    product: proMonthlyProduct!,
    featuresList: proFeaturesList,
    disabledTooltipContent: `You're currently on the Professional plan, billed annually. Click 'Update plan' at the bottom of the page to change your plan to Monthly.`,
  }

  const proYearlyPlan: IPlan = {
    product: proYearlyProduct!,
    featuresList: proFeaturesList,
    disabledTooltipContent: `You're currently on the Professional plan, billed monthly. Click 'Update plan' at the bottom of the page to upgrade your plan to Annual.`,
  }

  const [showUpdateSubscriptionModal, setShowUpdateSubscriptionModal] = useState(false)

  const hideUpdateSubscriptionModal = () => {
    setShowUpdateSubscriptionModal(false)
  }

  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false)

  const hideCancelSubscriptionModal = () => {
    setShowCancelSubscriptionModal(false)
  }

  const [showConfirmProAnnualModal, setShowConfirmProAnnualModal] = useState(false)

  const [onConfirmCallback, setOnConfirmCallback] = useState<(() => void) | null>(null)

  const addOrUpdateSubscription = (callback: () => void) => {
    if (selectedPlanId === proYearlyProduct?.id) {
      setOnConfirmCallback(() => callback)
      setShowConfirmProAnnualModal(true)
    } else {
      callback()
      setShowConfirmProAnnualModal(false)
    }
  }

  const filesMeteredProduct = subscriptionItems.find(
    (item) => item.product.metadata.productType === 'usage' && item.product.metadata.usageType === 'file'
  )

  const pdvMeteredProduct = subscriptionItems.find(
    (item) => item.product.metadata.productType === 'usage' && item.product.metadata.usageType === 'PDV'
  )

  const disableOverlay = currentPlan?.id === proYearlyProduct?.id

  const supportContactMsg = `Please contact support@flatfile to update your subscription.`

  return (
    <SelectedProductsContext.Provider
      value={{
        allProducts: productsData,
        selectedPlan: selectedPlanId,
        setSelectedPlan: (id) => {
          setSelectedPlanId(id)
          const fullPlan = getProduct(id, productsData)
          if (fullPlan && fullPlan.metadata.planType !== 'professional') {
            // Reset any pro-specific volume discounts to default
            const fileUsagePlan = productsData.find((product) => product.id === selectedFileUsageId)
            if (fileUsagePlan && fileUsagePlan.metadata.proOnly === 'true') {
              setSelectedFileUsageId(defaultFileUsageProduct?.id)
            }
            const pdvUsagePlan = productsData.find((product) => product.id === selectedPDVUsageId)
            if (pdvUsagePlan && pdvUsagePlan.metadata.proOnly) {
              setSelectedPDVUsageId(defaultPDVUsageProduct?.id)
            }
          }
        },
        selectedFileCommitment: selectedFileUsageId,
        setSelectedFileCommitment: (id) => {
          setSelectedFileUsageId(id)
        },
        selectedPDVCommitment: selectedPDVUsageId,
        setSelectedPDVCommitment: (id) => {
          setSelectedPDVUsageId(id)
        },
      }}
    >
      {billingEnabled ? (
        <>
          <Dialog open={showUpdateSubscriptionModal} onOpenChange={(open) => !open && hideUpdateSubscriptionModal()}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Update Subscription</DialogTitle>
              </DialogHeader>
              <UpdateSubscriptionModalContent
                {...{
                  subscriptionItems,
                  starterProduct,
                  productsData,
                  selectedPlanId,
                  selectedFileUsageId,
                  selectedPDVUsageId,
                  updateSubscription,
                  subscriptionUpdateIsError,
                  subscriptionUpdateIsLoading,
                  hideUpdateSubscriptionModal,
                }}
              />
            </DialogContent>
          </Dialog>

          <Dialog
            open={showConfirmProAnnualModal}
            onOpenChange={(open) => !open && setShowConfirmProAnnualModal(false)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Annual Plan</DialogTitle>
              </DialogHeader>
              <ConfirmProAnnualModalContent
                onClose={() => setShowConfirmProAnnualModal(false)}
                onConfirmCallback={onConfirmCallback}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={showCancelSubscriptionModal} onOpenChange={(open) => !open && hideCancelSubscriptionModal()}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cancel Subscription</DialogTitle>
              </DialogHeader>
              <CancelSubscriptionModalContent
                {...{
                  hideCancelSubscriptionModal,
                  cancelSubscription,
                  subscriptionCancellationIsError,
                  subscriptionCancellationIsLoading,
                }}
              />
            </DialogContent>
          </Dialog>

          <div className='max-h-full overflow-y-auto pb-48'>
            <BillingWrapper>
              {searchParams.get('checkoutSuccess') && (
                <Alert variant='outline'>
                  <AlertTitle>Thank you for subscribing to Flatfile!</AlertTitle>
                </Alert>
              )}
              {searchParams.get('cancelSuccess') && (
                <Alert variant='outline'>
                  <AlertTitle>Subscription canceled</AlertTitle>
                  <AlertDescription>
                    The pay-as-you-go option has been disabled for your account. Any prior usage charges will be billed
                    with the next billing cycle. You can continue to use the Flatfile Platform with your free monthly
                    usage allowance.
                  </AlertDescription>
                </Alert>
              )}
              {searchParams.get('updateSubscriptionSuccess') && (
                <Alert variant='outline'>
                  <AlertTitle>Your subscription has been updated!</AlertTitle>
                </Alert>
              )}
              <Text variant='title1' emphasized>
                Billing
              </Text>
              <Text>Review your usage, choose your plan and billing preferences or switch your team's plan</Text>

              {isGetProductsLoading ? (
                <div className='flex items-center justify-center'>
                  <Spinner />
                </div>
              ) : (
                <BillingContent>
                  <BillingUsage
                    hasSubscription={subscriptionItems.length > 0}
                    {...{
                      filesUsage,
                      pdvUsage,
                      freeFileLimit,
                      freePdvLimit,
                      filesMeteredProduct: filesMeteredProduct?.product || defaultFileUsageProduct!,
                      pdvMeteredProduct: pdvMeteredProduct?.product || defaultPDVUsageProduct!,
                    }}
                  />
                  {!disableSelfServe && (
                    <>
                      <div className='flex flex-col gap-4'>
                        <Text variant='title2' emphasized>
                          Plans
                        </Text>
                        <DisabledOverlay message={supportContactMsg} disable={disableOverlay}>
                          <div className='grid  gap-4'>
                            {((!currentPlan && subscriptionItems.length > 0) || PAYGEnabled) && (
                              <div
                                className={cn(
                                  'bg-white rounded p-6 hover:bg-gray-100',
                                  selectedPlanId === starterProduct?.id && 'border border-black'
                                )}
                                onClick={() => setSelectedPlanId(starterProduct?.id)}
                                role='button'
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setSelectedPlanId(starterProduct?.id)
                                  }
                                }}
                              >
                                <div className='flex justify-between items-center mb-4'>
                                  <div className='flex items-center gap-2'>
                                    <Text variant='title3' emphasized>
                                      STARTER
                                    </Text>
                                    {!currentPlan && subscriptionItems.length > 0 && <CurrentBadge />}
                                  </div>
                                  <div className='rounded-full h-4 w-4 border border-gray-300 flex items-center justify-center'>
                                    {selectedPlanId === starterProduct?.id && (
                                      <div className='rounded-full h-2 w-2 bg-primary'></div>
                                    )}
                                  </div>
                                </div>
                                <Text variant='title2' emphasized className='mb-2'>
                                  $0
                                  <Text as='span' className='text-sm'>
                                    /month
                                  </Text>
                                </Text>
                                <Text className='mb-4'>{starterPlan.subhead}</Text>
                                <Text className='mb-4'>{starterPlan.lead}</Text>
                                <div className='mt-4'>
                                  <Text emphasized className='mb-2'>
                                    {starterPlan.featuresList?.title}
                                  </Text>
                                  <ul className='list-disc pl-5 space-y-1'>
                                    {starterPlan.featuresList?.sections.flatMap((section, idx) => [
                                      section.title && (
                                        <Text key={`title-${idx}`} emphasized className='mt-2 mb-1'>
                                          {section.title}
                                        </Text>
                                      ),
                                      ...section.features.map((feature, fidx) => (
                                        <li key={`feature-${idx}-${fidx}`}>
                                          <Text>{feature}</Text>
                                        </li>
                                      )),
                                    ])}
                                  </ul>
                                </div>
                              </div>
                            )}

                            <div
                              className={cn(
                                'bg-white rounded p-6 hover:bg-gray-100',
                                (selectedPlanId === proMonthlyProduct?.id || selectedPlanId === proYearlyProduct?.id) &&
                                  'border border-black'
                              )}
                              onClick={() => setSelectedPlanId(proYearlyProduct?.id)}
                              role='button'
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setSelectedPlanId(proYearlyProduct?.id)
                                }
                              }}
                            >
                              <div className='flex justify-between items-center mb-4'>
                                <div className='flex items-center gap-2'>
                                  <Text variant='title3' emphasized>
                                    PROFESSIONAL
                                  </Text>
                                  {(currentPlan?.id === proMonthlyProduct?.id ||
                                    currentPlan?.id === proYearlyProduct?.id) && (
                                    <CurrentBadge
                                      disabled={currentPlan?.id !== selectedPlanId}
                                      disabledTooltipContent={
                                        currentPlan?.id === proMonthlyProduct?.id
                                          ? proMonthlyPlan.disabledTooltipContent
                                          : proYearlyPlan.disabledTooltipContent
                                      }
                                    />
                                  )}
                                </div>
                                <div className='rounded-full h-4 w-4 border border-gray-300 flex items-center justify-center'>
                                  {(selectedPlanId === proMonthlyProduct?.id ||
                                    selectedPlanId === proYearlyProduct?.id) && (
                                    <div className='rounded-full h-2 w-2 bg-primary'></div>
                                  )}
                                </div>
                              </div>
                              <Text variant='title2' emphasized className='mb-2'>
                                {selectedPlanId === proYearlyProduct?.id ? '$799' : '$999'}
                                <Text as='span' className='text-sm'>
                                  /month
                                </Text>
                              </Text>

                              <div
                                className='flex mt-4 mb-6 bg-gray-50 rounded-md p-1 border border-gray-200'
                                style={{ width: '25%', minWidth: '200px' }}
                              >
                                <button
                                  type='button'
                                  style={
                                    selectedPlanId === proMonthlyProduct?.id
                                      ? {
                                          backgroundColor: 'black',
                                          color: 'white',
                                          width: '50%',
                                          padding: '8px 0',
                                          textAlign: 'center',
                                          fontWeight: '500',
                                          borderRadius: '4px',
                                        }
                                      : {
                                          backgroundColor: 'transparent',
                                          color: '#4b5563',
                                          width: '50%',
                                          padding: '8px 0',
                                          textAlign: 'center',
                                          fontWeight: '500',
                                          borderRadius: '4px',
                                        }
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedPlanId(proMonthlyProduct?.id)
                                  }}
                                >
                                  Monthly
                                </button>
                                <button
                                  type='button'
                                  style={
                                    selectedPlanId === proYearlyProduct?.id
                                      ? {
                                          backgroundColor: 'black',
                                          color: 'white',
                                          width: '50%',
                                          padding: '8px 0',
                                          textAlign: 'center',
                                          fontWeight: '500',
                                          borderRadius: '4px',
                                        }
                                      : {
                                          backgroundColor: 'transparent',
                                          color: '#4b5563',
                                          width: '50%',
                                          padding: '8px 0',
                                          textAlign: 'center',
                                          fontWeight: '500',
                                          borderRadius: '4px',
                                        }
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedPlanId(proYearlyProduct?.id)
                                  }}
                                >
                                  Yearly
                                </button>
                              </div>

                              <div className='mt-4'>
                                <Text emphasized className='mb-2'>
                                  {proFeaturesList.title}
                                </Text>
                                <ul className='list-disc pl-5 space-y-1'>
                                  {proFeaturesList.sections.flatMap((section, idx) =>
                                    section.features.map((feature, fidx) => (
                                      <li key={`feature-${idx}-${fidx}`}>
                                        <Text>{feature}</Text>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </DisabledOverlay>
                      </div>
                      <DisabledOverlay message={supportContactMsg} disable={disableOverlay}>
                        <section>
                          <div
                            className={cn(
                              'bg-white rounded p-6 mt-4 hover:bg-gray-100',
                              selectedPlanId === undefined && 'border border-black'
                            )}
                            onClick={() => setSelectedPlanId(undefined)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setSelectedPlanId(undefined)
                              }
                            }}
                          >
                            <div className='flex justify-between items-center'>
                              <div className='flex items-center gap-2'>
                                <Text variant='title3' emphasized>
                                  FREE ALLOWANCE ONLY
                                </Text>
                                {currentPlan?.id === undefined &&
                                  currentFileCommitment === undefined &&
                                  currentPDVCommitment === undefined && <CurrentBadge />}
                              </div>

                              <div className='rounded-full h-4 w-4 border border-gray-300 flex items-center justify-center'>
                                {selectedPlanId === undefined && (
                                  <div className='rounded-full h-2 w-2 bg-primary'></div>
                                )}
                              </div>
                            </div>
                            <Text className='mt-4'>
                              Just use the Flatfile Platform without the option to use more than the monthly free
                              allowance. I understand that any data above the monthly allowance will not be processed.
                            </Text>
                          </div>
                        </section>
                      </DisabledOverlay>
                      <BillingSection>
                        <SectionHeader>Volume Discounts (Professional Plan Only)</SectionHeader>
                        <Text>
                          Pay for additional usage using our fixed-rate pay-as-you-go pricing, or pick a volume discount
                          with the Professional Plan.
                        </Text>
                        <Text>
                          {' '}
                          Looking for a beefier plan?{' '}
                          <a
                            target='_blank'
                            href='https://flatfile.com/contact-us'
                            rel='noreferrer'
                            className='text-primary hover:underline'
                          >
                            Contact us to learn about our Enterprise plan
                          </a>
                          .
                        </Text>
                        <DisabledOverlay message={supportContactMsg} disable={disableOverlay}>
                          <div className='grid grid-cols-2 gap-4 mt-4'>
                            <CommitmentVolumeSelector
                              products={fileUsageProducts}
                              usageType='file'
                              title='Files/month'
                              hasSubscription={!(selectedPlanId === undefined)}
                              currentCommitment={currentFileCommitment}
                              tooltipContent='Any file that is uploaded and extracted to a Flatfile Workbook in your production environment. File uploads to your dev environment are free.'
                            />
                            <CommitmentVolumeSelector
                              products={pdvUsageProducts}
                              usageType='PDV'
                              title='PDV/month'
                              hasSubscription={!(selectedPlanId === undefined)}
                              currentCommitment={currentPDVCommitment}
                              tooltipContent='PDV (Processed Data Values) are a fair and transparent way for us to measure big data files. PDVs are akin to cells in a spreadsheet, rows x columns that are created in a Flatfile Workbook in a calendar month (applies to production environments, dev environments are free). PDVs are displayed in millions (M).'
                            />
                          </div>
                        </DisabledOverlay>
                      </BillingSection>
                      {isCheckoutError && (
                        <Text>An error occurred. Please try again, or contact us if the problem persists.</Text>
                      )}

                      {!disableSelfServe && (
                        <div
                          className='fixed right-0 bottom-0 p-4 z-10 shadow-lg bg-surface-base-elevated'
                          style={{ width: 'calc(100vw - 275px)' }}
                        >
                          <div className='flex justify-between items-center px-4'>
                            <Text>
                              If you'd like to use ACH or an alternative payment method, please reach out to{' '}
                              <a href='mailto:support@flatfile.com' className='text-primary hover:underline'>
                                support@flatfile.com
                              </a>
                            </Text>
                            <div>
                              {!subscription ? (
                                <Button
                                  size='lg'
                                  onClick={() => {
                                    addOrUpdateSubscription(() => {
                                      handleCheckoutClick()
                                    })
                                  }}
                                  loading={isCheckoutLoading}
                                  disabled={lineItems.length === 0}
                                >
                                  Checkout
                                </Button>
                              ) : lineItems.length > 0 ? (
                                <Button
                                  size='lg'
                                  onClick={() => {
                                    addOrUpdateSubscription(() => setShowUpdateSubscriptionModal(true))
                                  }}
                                  disabled={noChange}
                                >
                                  Update plan
                                </Button>
                              ) : (
                                <Button
                                  size='lg'
                                  onClick={() => {
                                    addOrUpdateSubscription(() => setShowCancelSubscriptionModal(true))
                                  }}
                                >
                                  Cancel Subscription
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </BillingContent>
              )}
            </BillingWrapper>
          </div>
        </>
      ) : (
        <Text>
          Whoops! Billing features have not been enabled for your account. Please reach out to{' '}
          <a href='mailto:support@flatfile.com' className='text-primary hover:underline'>
            support@flatfile.com
          </a>{' '}
          to manage your subscription.
        </Text>
      )}
    </SelectedProductsContext.Provider>
  )
}
export const BillingPage = () => (
  <ErrorBoundary
    fallbackRender={({ error }) => (
      <ErrorBoundaryErrorPage
        error={error}
        page='billing.page.tsx'
        message='There was an error loading your billing data.'
      />
    )}
  >
    <div style={{ height: 'auto', overflow: 'auto', paddingBottom: '140px' }}>
      <BillingPageContent />
    </div>
  </ErrorBoundary>
)
